import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../services/apiClient';
import { useAuth } from '../stores/authStore';
import { SocialAuthButton } from '../components/auth/SocialAuthButton';
import { useFocusTrap } from '../lib/useFocusTrap';
import './AuthPage.css';

// Google sign-in is HIDDEN on the public login page for now (Microsoft + SSO
// remain). It can't be gated on "is platform admin" pre-login (no session yet),
// so testing is enabled via a flag the operator controls in their own browser:
//   • local dev → always shown
//   • visit /login?google=1 → reveals + persists it for this browser
//   • visit /login?google=0 → hides it again
function shouldShowGoogle(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('google') === '1') { localStorage.setItem('ws_show_google', '1'); return true; }
    if (q.get('google') === '0') { localStorage.removeItem('ws_show_google'); return false; }
    return localStorage.getItem('ws_show_google') === '1';
  } catch { return false; }
}

// ── SSO modal ─────────────────────────────────────────────────────────────
function SsoModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('auth');
  const [domain, setDomain] = useState('');
  const [slug, setSlug]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);

  // A11Y-1 — SsoModal is only ever mounted while open (the parent renders it
  // conditionally), so the trap is unconditionally active for its whole
  // lifetime; deactivation happens via unmount, which still restores focus.
  useFocusTrap(dialogRef, { active: true, onClose, initialFocusRef: domainInputRef });

  function initiateSso() {
    const d = domain.trim().toLowerCase().replace(/^@/, '');
    const s = slug.trim();
    if (!d && !s) { setErr(t('loginPage.sso.missingInputError')); return; }
    setBusy(true);
    // Navigate the top window directly so the IdP redirect works.
    const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:15001';
    const url = new URL(`${base}/api/v1/auth/saml`);
    if (d) url.searchParams.set('domain', d);
    else   url.searchParams.set('org', s);
    window.location.href = url.toString();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        className="ws-tile w-full max-w-sm p-6 space-y-4 relative"
        role="dialog"
        aria-modal="true"
        aria-label={t('loginPage.sso.dialogAriaLabel')}
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-ws-ink-3 hover:text-ws-ink transition-colors"
          onClick={onClose}
          aria-label={t('loginPage.sso.closeAriaLabel')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>

        <div>
          <h2 className="text-base font-semibold">{t('loginPage.sso.title')}</h2>
          <p className="text-xs text-ws-ink-3 mt-1">
            <Trans i18nKey="loginPage.sso.subtitle" ns="auth" components={{ em: <em /> }} />
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-ws-ink-2 mb-1">{t('loginPage.sso.domainLabel')}</label>
            <input
              ref={domainInputRef}
              className="ws-input w-full text-sm"
              placeholder={t('loginPage.sso.domainPlaceholder')}
              value={domain}
              onChange={e => { setDomain(e.target.value); setErr(null); }}
              onKeyDown={e => e.key === 'Enter' && initiateSso()}
            />
          </div>

          <div className="text-center text-xs text-ws-ink-3">{t('loginPage.sso.or')}</div>

          <div>
            <label className="block text-xs font-medium text-ws-ink-2 mb-1">{t('loginPage.sso.slugLabel')}</label>
            <input
              className="ws-input w-full text-sm"
              placeholder={t('loginPage.sso.slugPlaceholder')}
              value={slug}
              onChange={e => { setSlug(e.target.value); setErr(null); }}
              onKeyDown={e => e.key === 'Enter' && initiateSso()}
            />
          </div>
        </div>

        {err && <p className="text-xs text-ws-danger">{err}</p>}

        <button
          type="button"
          className="btn btn--accent w-full py-2.5 text-sm"
          onClick={initiateSso}
          disabled={busy}
        >
          {busy ? t('loginPage.sso.redirecting') : t('loginPage.sso.continueButton')}
        </button>
      </div>
    </div>
  );
}

const LS_LAST_LOGIN  = 'worksnip.lastLoginAt';

// Backend error codes handled by the shared `errors:*` catalog. Keeps the
// inline error line human-readable instead of dumping `email_taken` at the
// user. See translated-strings report (WP-1A) for the full {code, text} list.
const KNOWN_AUTH_ERROR_CODES = ['invalid_credentials', 'email_taken', 'missing_fields', 'invalid_org_name', 'rate_limited'];

export function LoginPage() {
  const { t } = useTranslation(['auth', 'errors']);

  function friendlyAuthError(code: string | undefined): string {
    if (code && KNOWN_AUTH_ERROR_CODES.includes(code)) return t(`errors:${code}`);
    return code || t('loginPage.genericError');
  }

  const nav = useNavigate();
  const setAuth = useAuth(s => s.setAuth);
  const [searchParams] = useSearchParams();
  const nextParam  = searchParams.get('next');
  const emailParam = searchParams.get('email');
  // The marketing site's "Sign up" CTA points at /signup; that route renders
  // this same page (LoginPage is the combined login+register surface). Start in
  // register mode when arriving via /signup (or with an ?email= prefill).
  const isSignupPath = typeof window !== 'undefined' && /\/signup\b/.test(window.location.pathname);

  const [mode, setMode] = useState<'login' | 'register'>((emailParam || isSignupPath) ? 'register' : 'login');
  const [email, setEmail]             = useState(emailParam || '');
  const [password, setPassword]       = useState('');
  const [displayName, setDisplayName] = useState('');
  const [orgName, setOrgName]         = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [busy, setBusy]               = useState(false);
  const [err, setErr]                 = useState<string | null>(null);
  const [toast, setToast]             = useState<string | null>(null);
  const [showSso, setShowSso]         = useState(false);
  const [showGoogle]                  = useState(shouldShowGoogle);

  // Verification-pending state. Set true when register returns
  // `verification_required` OR login returns 403 `email_not_verified`. The
  // shell pivots to a "check your email" card with a Resend button.
  const [verifyPending, setVerifyPending] = useState(false);
  const [verifyEmail, setVerifyEmail]     = useState('');
  // Show a resend affordance inline (without the full pivot) when the user
  // hit `email_taken` — they may have an unverified account.
  const [showInlineResend, setShowInlineResend] = useState(false);
  // Client-side resend cooldown. Mirrors the backend's 60s per-email cooldown
  // so the button visibly disables for 60s after each send — the user isn't
  // left guessing whether it worked or hammering it into the rate limiter.
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendBusy, setResendBusy]         = useState(false);
  const [resendNote, setResendNote]         = useState<string | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(s => {
        if (s <= 1 && cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = null; }
        return s - 1;
      });
    }, 1000);
    return () => { if (cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = null; } };
  }, [resendCooldown > 0]);

  const switchMode = (m: 'login' | 'register') => {
    setMode(m);
    setErr(null);
    setToast(null);
    setVerifyPending(false);
    setShowInlineResend(false);
  };

  /** Fire the resend-verification request. The backend always returns 200
   *  (to avoid leaking account existence) so we optimistically start the
   *  60s cooldown regardless. A 429 means the IP rate-limit tripped. */
  async function resendVerification(targetEmail: string) {
    if (resendBusy || resendCooldown > 0) return;
    setResendBusy(true);
    setResendNote(null);
    try {
      await api.post('/api/v1/auth/resend-verification', { email: targetEmail });
      setResendNote(t('shared.resendSentNote'));
      setResendCooldown(60);
    } catch (e: any) {
      const code = e.response?.data?.error;
      if (code === 'rate_limited') {
        setResendNote(t('errors:rate_limited'));
        setResendCooldown(120);
      } else {
        setResendNote(t('shared.resendFailedNote'));
      }
    } finally {
      setResendBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setShowInlineResend(false);

    try {
      if (mode === 'login') {
        const { data } = await api.post('/api/v1/auth/login', { email, password, remember: keepSignedIn });
        window.localStorage.setItem(LS_LAST_LOGIN, new Date().toISOString());
        setAuth({
          token: data.token,
          user: data.user,
          organization: data.organization,
        });
        nav(nextParam || '/dashboard');
      } else {
        // camelCase keys — the backend destructures `displayName` / `orgName`
        // (it now also accepts the old snake_case spellings, but this is the
        // canonical contract). orgName is OPTIONAL: blank → the backend names
        // the workspace "{name}'s workspace" exactly as before.
        const { data } = await api.post('/api/v1/auth/register', {
          email,
          password,
          displayName: displayName.trim(),
          orgName: orgName.trim() || undefined,
        });
        // Backend gates password signups behind email verification: it returns
        // { ok, verification_required, email } and NO token. Pivot to the
        // check-your-email card instead of trying to setAuth with undefined.
        if (data.verification_required) {
          setVerifyEmail(data.email || email);
          setVerifyPending(true);
          setResendCooldown(60); // the register call already sent one email
          return;
        }
        // Fallback: older flow that returns a token directly (federated / dev).
        if (data.token) {
          setAuth({ token: data.token, user: data.user, organization: data.organization });
          nav(nextParam || '/dashboard');
        }
      }
    } catch (e: any) {
      const status = e.response?.status;
      const code   = e.response?.data?.error;
      // Login against an unverified account → pivot to the resend card.
      if (mode === 'login' && status === 403 && code === 'email_not_verified') {
        setVerifyEmail(e.response?.data?.email || email);
        setVerifyPending(true);
        return;
      }
      // Register with an existing (possibly unverified) email → show the
      // inline resend affordance alongside the error.
      if (mode === 'register' && status === 409 && code === 'email_taken') {
        setShowInlineResend(true);
      }
      setErr(friendlyAuthError(code) || e.message || t('loginPage.genericError'));
    } finally {
      setBusy(false);
    }
  }

  // ── Verification-pending card (shared across login + register modes) ──
  if (verifyPending) {
    return (
      <div className="auth-body login-body">
        <div className="login-shell">
          <main className="login-main" style={{ maxWidth: 480 }}>
            <a href="/" aria-label={t('loginPage.login.wordmarkHomeAriaLabel')} style={{ marginBottom: 'auto' }}>
              <span className="wordmark" aria-label={t('loginPage.login.wordmarkAriaLabel')} style={{ fontSize: '28px' }}>
                {/* eslint-disable-next-line i18next/no-literal-string -- decorative "ww" wordmark glyph, not translatable content */}
                <span className="w-ink">w</span><span className="w-teal" aria-hidden="true">w</span>
              </span>
            </a>
            <h1><Trans i18nKey="loginPage.verifyPending.title" ns="auth" components={{ serif: <span className="serif" /> }} /></h1>
            <p className="lead">
              <Trans i18nKey="loginPage.verifyPending.body" ns="auth" values={{ email: verifyEmail }} components={{ b: <b /> }} />
            </p>

            <div style={{ marginTop: 24, padding: '16px 18px', borderRadius: 12, background: 'var(--ws-surface-2)', border: '1px solid var(--ws-border)' }}>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
                {t('loginPage.verifyPending.noEmailNote')}
              </div>
              <button
                type="button"
                className="btn btn--accent"
                style={{ width: '100%', padding: '12px 18px', fontSize: 14 }}
                disabled={resendBusy || resendCooldown > 0}
                onClick={() => void resendVerification(verifyEmail)}
              >
                {resendBusy
                  ? t('shared.resendSendingButton')
                  : resendCooldown > 0
                    ? t('shared.resendCooldownButton', { seconds: resendCooldown })
                    : t('shared.resendVerificationButton')}
              </button>
              {resendNote && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>{resendNote}</div>
              )}
            </div>

            <div className="login-foot" style={{ marginTop: 'auto' }}>
              {t('loginPage.verifyPending.alreadyVerified')} <button type="button" onClick={() => switchMode('login')} className="text-ws-ink font-medium hover:text-ws-accent-deep transition-colors">{t('loginPage.verifyPending.backToSignIn')}</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (mode === 'login') {
    return (
      <div className="auth-body login-body">
        <div className="login-shell">
        <main className="login-main">
          <a href="/" aria-label={t('loginPage.login.wordmarkHomeAriaLabel')} style={{ marginBottom: 'auto' }}>
            <span className="wordmark" aria-label={t('loginPage.login.wordmarkAriaLabel')} style={{ fontSize: '28px' }}>
              {/* eslint-disable-next-line i18next/no-literal-string -- decorative "ww" wordmark glyph, not translatable content */}
              <span className="w-ink">w</span><span className="w-teal" aria-hidden="true">w</span>
            </span>
          </a>

          <h1><Trans i18nKey="loginPage.login.title" ns="auth" components={{ serif: <span className="serif" /> }} /></h1>
          <p className="lead">{t('loginPage.login.lead')}</p>

          <form id="loginForm" autoComplete="on" onSubmit={submit}>
            <div className="field">
              <label htmlFor="email">{t('shared.workEmailLabel')}</label>
              <input id="email" name="email" type="email" autoComplete="email" placeholder={t('shared.workEmailPlaceholder')} autoFocus required value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <div className="field-row">
                <label htmlFor="password">{t('shared.passwordLabel')}</label>
                <a className="forgot" href="#">{t('loginPage.login.forgotPassword')}</a>
              </div>
              <input id="password" name="password" type="password" autoComplete="current-password" placeholder="••••••••••" required value={password} onChange={e => setPassword(e.target.value)} />
            </div>

            <button className="btn btn--accent" type="submit" style={{ width: '100%', padding: '15px 22px', fontSize: '15px', marginTop: '6px' }} disabled={busy}>
              {busy ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="9" strokeDasharray="40" strokeDashoffset="20">
                      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                    </circle>
                  </svg> {t('loginPage.login.signingIn')}
                </>
              ) : (
                <>
                  {t('loginPage.login.submitButton')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </>
              )}
            </button>

            {err && <div role="alert" style={{ color: 'var(--ws-danger)', fontSize: '13px', marginTop: '4px' }}>{err}</div>}

            <div className="divider">{t('loginPage.login.divider')}</div>

            <div className="oauth-row">
              {showGoogle && <SocialAuthButton provider="google" onMessage={setToast} />}
              <SocialAuthButton provider="microsoft" onMessage={setToast} />
            </div>

            <button
              type="button"
              className="btn btn--ghost w-full text-sm py-2.5 mt-1"
              onClick={() => setShowSso(true)}
            >
              {t('loginPage.login.ssoButton')}
            </button>
          </form>

          {showSso && <SsoModal onClose={() => setShowSso(false)} />}

          <div className="login-foot">
            {t('loginPage.login.footerPrompt')} <button type="button" onClick={() => switchMode('register')} className="text-ws-ink font-medium hover:text-ws-accent-deep transition-colors">{t('loginPage.login.footerAction')}</button>
          </div>
        </main>

        <aside className="login-side">
          <div>
            <span className="tag"><span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)' }}></span>{t('loginPage.login.whatsNewTag')}</span>
            <h2><Trans i18nKey="loginPage.login.whatsNewTitle" ns="auth" components={{ serif: <span className="serif" /> }} /></h2>
          </div>

          <div className="preview">
            <div style={{ fontSize: '11px', color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '10px' }}>{t('loginPage.login.previewEyebrow')}</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3, marginBottom: '12px' }}>{t('loginPage.login.previewGuideTitle')}</div>
            <div className="preview-step live">
              <div className="n">12</div>
              <div>
                <div className="t">{t('loginPage.login.previewStep1Title')}</div>
                <div className="d">{t('loginPage.login.previewStep1Desc')}</div>
              </div>
            </div>
            <div className="preview-step">
              <div className="n">11</div>
              <div>
                <div className="t">{t('loginPage.login.previewStep2Title')}</div>
                <div className="d">{t('loginPage.login.previewStep2Desc')}</div>
              </div>
            </div>
            <div className="preview-step">
              <div className="n">10</div>
              <div>
                <div className="t">{t('loginPage.login.previewStep3Title')}</div>
                <div className="d">{t('loginPage.login.previewStep3Desc')}</div>
              </div>
            </div>
          </div>

          <p className="meta">{t('loginPage.login.asideMeta')}</p>
        </aside>
      </div>
      </div>
    );
  }

  // Register Mode
  return (
    <div className="signup-body">
      <main className="auth-main">
        {/* .btn forces white-space:nowrap; at 375px "Already have an account?
            Log in" next to the wordmark is wider than the viewport and forces
            page-level horizontal scroll. flex-wrap + whiteSpace:normal let it
            drop to its own line / wrap instead — desktop has room so neither
            kicks in there. */}
        <div className="auth-top" style={{ flexWrap: 'wrap', rowGap: 8 }}>
          {/* eslint-disable-next-line i18next/no-literal-string -- decorative "ww" wordmark glyph, not translatable content */}
          <a href="/" aria-label={t('loginPage.login.wordmarkHomeAriaLabel')}><span className="wordmark" aria-label={t('loginPage.login.wordmarkAriaLabel')} style={{ fontSize: '30px' }}><span className="w-ink">w</span><span className="w-teal" aria-hidden="true">w</span></span></a>
          <button type="button" className="btn btn--link text-ws-ink-2" style={{ whiteSpace: 'normal', textAlign: 'right' }} onClick={() => switchMode('login')}>{t('loginPage.register.backToLoginPrompt')} <b style={{ color: 'var(--ink)' }}>{t('loginPage.register.backToLoginAction')}</b></button>
        </div>

        <div className="auth-card">
          <h1><Trans i18nKey="loginPage.register.title" ns="auth" components={{ serif: <span className="serif" /> }} /></h1>
          <p className="lead" style={{ marginBottom: '16px' }}>{t('loginPage.register.lead')}</p>

          <form id="signupForm" autoComplete="on" onSubmit={submit}>
            <div className="oauth-row">
              {showGoogle && <SocialAuthButton provider="google" onMessage={setToast} />}
              <SocialAuthButton provider="microsoft" onMessage={setToast} />
            </div>

            <div className="divider">{t('loginPage.register.dividerEmail')}</div>

            <div className="field">
              <label htmlFor="name">{t('loginPage.register.nameLabel')}</label>
              <input id="name" name="name" type="text" autoComplete="name" placeholder={t('loginPage.register.namePlaceholder')} required value={displayName} onChange={e => setDisplayName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="email">{t('shared.workEmailLabel')}</label>
              <input id="email" name="email" type="email" autoComplete="email" placeholder={t('shared.workEmailPlaceholder')} required value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            {/* Optional company/workspace name. The placeholder previews the exact
                default the backend applies when it's left blank, so skipping the
                field is visibly safe. */}
            <div className="field">
              <label htmlFor="orgName">{t('loginPage.register.orgNameLabel')} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{t('loginPage.register.orgNameOptionalTag')}</span></label>
              <input
                id="orgName"
                name="organization"
                type="text"
                autoComplete="organization"
                maxLength={100}
                placeholder={t('shared.defaultWorkspaceName', { name: displayName.trim() || email.split('@')[0] || t('shared.yourNameFallback') })}
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
              />
              <span className="hint">{t('loginPage.register.orgNameHint')}</span>
            </div>
            <div className="field">
              <label htmlFor="password">{t('shared.passwordLabel')}</label>
              <input id="password" name="password" type="password" autoComplete="new-password" placeholder={t('loginPage.register.passwordPlaceholder')} minLength={10} required value={password} onChange={e => setPassword(e.target.value)} />
            </div>

            <div className="checkbox-row">
              <input id="updates" type="checkbox" defaultChecked />
              <label htmlFor="updates">{t('loginPage.register.updatesOptIn')}</label>
            </div>

            <button className="btn btn--accent" type="submit" style={{ width: '100%', padding: '16px 22px', fontSize: '15px' }} disabled={busy}>
              {busy ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9" strokeDasharray="40" strokeDashoffset="20"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg> {t('loginPage.register.creatingWorkspace')}
                </>
              ) : (
                <>
                  {t('loginPage.register.submitButton')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </>
              )}
            </button>

            {err && <div role="alert" style={{ color: 'var(--ws-danger)', fontSize: '13px', marginTop: '10px' }}>{err}</div>}

            {showInlineResend && (
              <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 10, background: 'var(--ws-surface-2)', border: '1px solid var(--ws-border)' }}>
                <button
                  type="button"
                  className="btn btn--link text-ws-ink"
                  style={{ fontWeight: 600 }}
                  disabled={resendBusy || resendCooldown > 0}
                  onClick={() => void resendVerification(email)}
                >
                  {resendBusy
                    ? t('shared.resendSendingButton')
                    : resendCooldown > 0
                      ? t('shared.resendCooldownButton', { seconds: resendCooldown })
                      : t('shared.resendVerificationButton')}
                </button>
                {resendNote && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>{resendNote}</div>}
              </div>
            )}

            <p className="auth-foot" style={{ marginTop: '12px' }}>
              <Trans
                i18nKey="loginPage.register.termsNotice"
                ns="auth"
                components={{
                  terms: <a href="#" className="hover:underline" />,
                  privacy: <a href="#" className="hover:underline" />,
                }}
              />
            </p>
          </form>
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--ink-3)', paddingTop: '16px' }}>
          <span>{t('loginPage.register.footerCopyright')}</span>
          <a href="#" className="hover:text-ws-ink">{t('loginPage.register.footerHelp')}</a>
        </div>
      </main>

      <aside className="auth-aside" aria-hidden="true">
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow on-dark"><span className="dot"></span>{t('loginPage.register.asideEyebrow')}</span>
          <ul className="aside-feats">
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="m5 12 5 5L20 7"/></svg>
              <span>{t('loginPage.register.featureSidePanel')}</span>
            </li>
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="m5 12 5 5L20 7"/></svg>
              <span><Trans i18nKey="loginPage.register.featurePricing" ns="auth" components={{ b: <b style={{ color: '#fff' }} /> }} /></span>
            </li>
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="m5 12 5 5L20 7"/></svg>
              <span>{t('loginPage.register.featureVoice')}</span>
            </li>
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="m5 12 5 5L20 7"/></svg>
              <span>{t('loginPage.register.featureSpecialtyPack')}</span>
            </li>
          </ul>
        </div>

        <div className="aside-mock">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* eslint-disable-next-line i18next/no-literal-string -- decorative side-panel mark glyph, not translatable content */}
              <span className="wordmark-mark" style={{ background: '#1A1D21' }} aria-hidden="true">k</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{t('loginPage.register.mockPanelLabel')}</span>
            </div>
            <span className="live">{t('loginPage.register.mockRecLabel')}</span>
          </div>
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: '12px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
            <b style={{ color: '#fff' }}>{t('loginPage.register.mockStepLabel')}</b> <Trans i18nKey="loginPage.register.mockStepDesc" ns="auth" components={{ accent: <span style={{ color: 'var(--accent)' }} /> }} />
          </div>
        </div>

        <blockquote className="aside-quote">
          {t('loginPage.register.testimonialQuote')}
          <div className="aside-quote-author">{t('loginPage.register.testimonialAuthor')}</div>
        </blockquote>
      </aside>
    </div>
  );
}
